// 261005 E3-P4a — hộp thoại sửa chương thô chặng ①: tải lại (xem trước) · tách (chọn ứng viên) · gộp/xoá (xác nhận kèm tác động) · phiên bản chương.
// Mọi thao tác: gọi `dryRun` lấy `impact` trước khi áp. Logic hiển thị ở `../sourceChapterUtils.ts` (thuần, có test).
import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, List, Modal, Popconfirm, Radio, Space, Spin, Tag, Typography, message } from 'antd';
import { adaptationApi } from '../../../services/api/adaptation';
import type { RawChapter, RawChapterImpact, RawChapterReloadPreview, RawChapterSplitCandidates, RawChapterVersionMeta } from '../../../types/adaptation';
import { formatShortDateTime } from '../adaptationUtils';
import { versionNoteText, candidatesNearMiddle, impactLines, latestVersionId, rawErrorCode, rawErrorText, reloadWarningText, versionOpLabel } from '../sourceChapterUtils';

const { Text, Paragraph } = Typography;

interface BaseProps { chapter: RawChapter | null; onClose: () => void; onChanged: () => Promise<unknown> | void }

const ImpactAlert: React.FC<{ impact: RawChapterImpact | null }> = ({ impact }) => {
  const lines = impactLines(impact);
  if (!lines.length) return impact ? <Text type="secondary" style={{ fontSize: 13 }}>Không ảnh hưởng quyển đã quét hay danh sách tập.</Text> : null;
  return <Alert type="warning" showIcon message="Sau thao tác này" description={<ul style={{ margin: 0, paddingLeft: 18 }}>{lines.map((l) => <li key={l}>{l}</li>)}</ul>} />;
};

/** Báo lỗi chuẩn của thao tác; trả true nếu cần đóng + tải lại (mốc CAS lệch). */
const handleOpError = (e: any): boolean => {
  const code = rawErrorCode(e);
  if (code === 'CHAPTER_CHANGED' || code === 'CHAPTERS_CHANGED') { message.warning('Chương vừa đổi ở nơi khác — đã tải lại, thử lại', 6); return true; }
  message.error(rawErrorText(e), 8);
  return false;
};

export const ReloadChapterModal: React.FC<BaseProps> = ({ chapter, onClose, onChanged }) => {
  const [pv, setPv] = useState<RawChapterReloadPreview | null>(null);
  const [impact, setImpact] = useState<RawChapterImpact | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!chapter) return;
    let alive = true;
    setPv(null); setImpact(null); setErr(null); setBusy(true);
    (async () => {
      try {
        const p = await adaptationApi.rawChapterReloadPreview(chapter.id);
        if (!alive) return;
        setPv(p);
        const d = await adaptationApi.rawChapterReload(chapter.id, { content: p.content, contentSha: p.contentSha, expectedModifiedAt: p.modifiedAt, dryRun: true });
        if (alive) setImpact(d.impact);
      } catch (e: any) { if (alive) setErr(rawErrorText(e, 'Không tải được trang nguồn')); }
      finally { if (alive) setBusy(false); }
    })();
    return () => { alive = false; };
  }, [chapter]);
  if (!chapter) return null;
  const save = async () => {
    if (!pv) return;
    setBusy(true);
    try {
      await adaptationApi.rawChapterReload(chapter.id, { content: pv.content, contentSha: pv.contentSha, expectedModifiedAt: pv.modifiedAt });
      message.success(`Đã tải lại chương ${chapter.orderIndex}`);
      onClose(); await onChanged();
    } catch (e) { if (handleOpError(e)) { onClose(); await onChanged(); } } finally { setBusy(false); }
  };
  return (
    <Modal open title={`Tải lại chương ${chapter.orderIndex} từ link`} okText="Lưu" cancelText="Huỷ" onOk={save} onCancel={onClose} width={720}
      okButtonProps={{ disabled: !pv || busy || !!err }} confirmLoading={busy && !!pv} destroyOnClose>
      <Space direction="vertical" style={{ width: '100%' }} size={8}>
        {err && <Alert type="error" showIcon message={err} />}
        {!pv && !err && <Spin />}
        {pv && (
          <>
            <Text>Độ dài: <b>{pv.oldCharCount.toLocaleString()}</b> → <b>{pv.charCount.toLocaleString()}</b> ký tự (giữ tên chương cũ)</Text>
            {reloadWarningText(pv) && <Alert type={pv.warning ? 'warning' : 'info'} showIcon message={reloadWarningText(pv)} />}
            {!pv.ok && <Alert type="warning" showIcon message="Bản tải về trông chưa ổn" description={pv.reasons.join(' · ')} />}
            <Text strong>Đầu</Text><Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }} ellipsis={{ rows: 6 }}>{pv.head}</Paragraph>
            <Text strong>Cuối</Text><Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }} ellipsis={{ rows: 6 }}>{pv.tail}</Paragraph>
            <ImpactAlert impact={impact} />
          </>
        )}
      </Space>
    </Modal>
  );
};

export const SplitChapterModal: React.FC<BaseProps> = ({ chapter, onClose, onChanged }) => {
  const [data, setData] = useState<RawChapterSplitCandidates | null>(null);
  const [offset, setOffset] = useState<number | null>(null);
  const [impact, setImpact] = useState<RawChapterImpact | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!chapter) return;
    let alive = true;
    setData(null); setOffset(null); setImpact(null); setBusy(true);
    adaptationApi.rawChapterSplitCandidates(chapter.id).then((r) => { if (alive) setData(r); }).catch((e) => { if (alive) message.error(rawErrorText(e), 6); }).finally(() => { if (alive) setBusy(false); });
    return () => { alive = false; };
  }, [chapter]);
  const shown = useMemo(() => (data ? candidatesNearMiddle(data.candidates, data.length) : []), [data]);
  useEffect(() => {
    if (!chapter || !data || offset === null) { setImpact(null); return; }
    let alive = true;
    adaptationApi.rawChapterSplit(chapter.id, { offset, expectedModifiedAt: data.modifiedAt, dryRun: true }).then((r) => { if (alive) setImpact(r.impact); }).catch(() => { if (alive) setImpact(null); });
    return () => { alive = false; };
  }, [chapter, data, offset]);
  if (!chapter) return null;
  const apply = async () => {
    if (!data || offset === null) return;
    setBusy(true);
    try {
      await adaptationApi.rawChapterSplit(chapter.id, { offset, expectedModifiedAt: data.modifiedAt });
      message.success(`Đã tách chương ${chapter.orderIndex}`);
      onClose(); await onChanged();
    } catch (e) { if (handleOpError(e)) { onClose(); await onChanged(); } } finally { setBusy(false); }
  };
  return (
    <Modal open title={`Tách chương ${chapter.orderIndex}`} okText="Tách" cancelText="Huỷ" onOk={apply} onCancel={onClose} width={720}
      okButtonProps={{ disabled: offset === null || busy }} confirmLoading={busy && offset !== null} destroyOnClose>
      <Space direction="vertical" style={{ width: '100%' }} size={8}>
        {!data && <Spin />}
        {data && shown.length === 0 && <Alert type="info" showIcon message="Chương không có chỗ tách hợp lệ (cần xuống dòng hoặc hết câu)." />}
        {data && shown.length > 0 && (
          <>
            <Text type="secondary" style={{ fontSize: 13 }}>Chọn chỗ cắt (quanh giữa chương, dài {data.length.toLocaleString()} ký tự). Cả hai nửa mất link nguồn.</Text>
            <Radio.Group value={offset} onChange={(e) => setOffset(e.target.value)} style={{ width: '100%' }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                {shown.map((c) => (
                  <Radio key={c.offset} value={c.offset} style={{ whiteSpace: 'normal' }}>
                    <Text type="secondary">…{c.before.replace(/\n/g, '↵')}</Text><Tag color="blue" style={{ margin: '0 4px' }}>✂ {c.offset.toLocaleString()}</Tag><Text>{c.after.replace(/\n/g, '↵')}…</Text>
                  </Radio>
                ))}
              </Space>
            </Radio.Group>
            <ImpactAlert impact={impact} />
          </>
        )}
      </Space>
    </Modal>
  );
};

/** Gộp với chương sau / Xoá — xác nhận kèm tác động. */
export const ConfirmChapterOpModal: React.FC<BaseProps & { kind: 'merge' | 'delete' }> = ({ chapter, kind, onClose, onChanged }) => {
  const [impact, setImpact] = useState<RawChapterImpact | null>(null);
  const [busy, setBusy] = useState(false);
  const call = (dryRun: boolean) => kind === 'merge'
    ? adaptationApi.rawChapterMergeNext(chapter!.id, { expectedModifiedAt: chapter!.modifiedAt, dryRun })
    : adaptationApi.deleteRawChapter(chapter!.id, { expectedModifiedAt: chapter!.modifiedAt, dryRun });
  useEffect(() => {
    if (!chapter) return;
    let alive = true;
    setImpact(null);
    call(true).then((r) => { if (alive) setImpact(r.impact); }).catch((e) => { if (alive) message.error(rawErrorText(e), 6); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapter, kind]);
  if (!chapter) return null;
  const apply = async () => {
    setBusy(true);
    try {
      await call(false);
      message.success(kind === 'merge' ? `Đã gộp chương ${chapter.orderIndex} với chương sau` : `Đã xoá chương ${chapter.orderIndex}`);
      onClose(); await onChanged();
    } catch (e) { if (handleOpError(e)) { onClose(); await onChanged(); } } finally { setBusy(false); }
  };
  return (
    <Modal open title={kind === 'merge' ? `Gộp chương ${chapter.orderIndex} với chương sau?` : `Xoá chương ${chapter.orderIndex}?`}
      okText={kind === 'merge' ? 'Gộp' : 'Xoá'} okButtonProps={{ danger: kind === 'delete', disabled: impact === null }} cancelText="Huỷ"
      onOk={apply} onCancel={onClose} confirmLoading={busy} destroyOnClose>
      <Space direction="vertical" style={{ width: '100%' }} size={8}>
        <Text type="secondary">Các chương phía sau đổi số. Khôi phục được ở mục "Phiên bản chương" (chỉ bản mới nhất).</Text>
        {impact === null ? <Spin /> : <ImpactAlert impact={impact} />}
      </Space>
    </Modal>
  );
};

export const ChapterVersionsPanel: React.FC<{ storyId: string; refreshKey: number | string; disabled?: boolean; onChanged: () => Promise<unknown> | void }> = ({ storyId, refreshKey, disabled, onChanged }) => {
  const [rows, setRows] = useState<RawChapterVersionMeta[]>([]);
  const [busy, setBusy] = useState(false);
  const [restoreOpenId, setRestoreOpenId] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    adaptationApi.rawChapterVersions(storyId).then((r) => { if (alive) setRows(r); }).catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, [storyId, refreshKey]);
  if (!rows.length) return null;
  const latest = latestVersionId(rows);
  const restore = async (id: string) => {
    setBusy(true);
    try {
      await adaptationApi.rawChapterVersionRestore(id);
      message.success('Đã khôi phục');
      await onChanged();
    } catch (e) { message.error(rawErrorText(e), 8); } finally { setBusy(false); }
  };
  return (
    <div style={{ marginTop: 12 }}>
      <Text strong>Phiên bản chương</Text>
      <List
        size="small" dataSource={rows.slice(0, 8)}
        renderItem={(v) => (
          <List.Item actions={[
            v.id === latest ? (
              <Popconfirm
                key="r" title="Khôi phục lại trước thao tác này?" okText="Khôi phục" cancelText="Huỷ" disabled={disabled}
                open={restoreOpenId === v.id} onOpenChange={(open) => setRestoreOpenId(open ? v.id : null)}
                onConfirm={() => { setRestoreOpenId(null); return restore(v.id); }}
              >
                <Button size="small" type="link" disabled={disabled || busy}>Khôi phục</Button>
              </Popconfirm>
            ) : <Text key="x" type="secondary" style={{ fontSize: 13 }}>chỉ bản mới nhất</Text>,
          ]}>
            <Text>#{v.versionNo} · {versionOpLabel(v.op)}{v.note ? ` — ${versionNoteText(v.note)}` : ''}</Text>
            <Text type="secondary" style={{ fontSize: 13, marginLeft: 8 }}>{formatShortDateTime(v.createdAt)}</Text>
          </List.Item>
        )}
      />
    </div>
  );
};
